import React from 'react';
import { DocsLayout } from '../components/DocsLayout';
import { ComparisonTable } from '../components/ComparisonTable';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Building2,
  MapPin,
  Search,
  Eye,
  FileText,
  Info,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

export const DealersGuidePage: React.FC = () => {
  return (
    <DocsLayout
      title='Dealer Management'
      description='Manage dealer locations and service areas'
      breadcrumbs={[
        { label: 'Documentation', path: '/docs' },
        { label: 'Features', path: '/docs/features' },
        { label: 'Dealer Management' }
      ]}
    >
      <div className='max-w-[1000px]'>
        {/* Overview Section */}
        <section className='mb-20' id='overview'>
          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl mb-10'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h3 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3'>
                  What is Dealer Management?
                </h3>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Dealer Management is where you control which dealership locations appear in your
                  system. You can include dealers you work with regularly, exclude ones that are
                  temporarily closed or don't want mobile service, and add notes about each
                  location. Think of it as your "dealership address book" where you control who's
                  visible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Dealer Management Matters Section */}
        <section className='mb-20' id='why-dealer-management-matters'>
          <h2 className='text-3xl font-bold mb-6'>Why Dealer Management Matters</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            If you serve multiple dealerships or have locations that come and go, Dealer Management
            helps you keep your recall lists and maps clean and accurate:
          </p>

          <div className='grid gap-6 sm:grid-cols-2'>
            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Eye className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Control Visibility</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Only show dealers you are actively serving. Hide closed locations, competitors, or
                  dealers you don't have contracts with.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <MapPin className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Clean Up Your Map</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  When you view recalls on the map, you only see pins for dealers you actually work
                  with. No clutter from irrelevant locations.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <FileText className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Track Important Info</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Add notes about each dealer: contact preferences, access codes, parking
                  instructions, or special requirements.
                </p>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardHeader className='pb-4'>
                <div className='flex items-center gap-4 mb-2'>
                  <div className='p-3 bg-primary/10 rounded-xl'>
                    <Search className='h-8 w-8 text-primary' />
                  </div>
                  <CardTitle className='text-lg font-bold'>Find Dealers Fast</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Search by dealer name with flexible matching options. Find what you need quickly
                  even with partial names.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* How to Access Section */}
        <section className='mb-20' id='how-to-access-dealer-management'>
          <h2 className='text-3xl font-bold mb-6 flex items-center gap-3'>
            <Building2 className='h-8 w-8 text-primary' />
            How to Access Dealer Management
          </h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Follow these steps to access the Dealer Management page:
          </p>

          <div className='space-y-6'>
            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                1
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Open the Sidebar</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Look at the left side of your screen for the main navigation menu.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                2
              </div>
              <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Find Configuration Section
                </h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Scroll until you see the "Configuration" heading. This is where you manage system
                  settings.
                </p>
              </div>
            </div>

            <div className='relative flex gap-4'>
              <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                3
              </div>
              <div className='flex-1 pb-2'>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Click Dealers</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Under Configuration, click "Dealers". You will see your complete dealer directory.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-amber-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2'>
                  Who Can Access This?
                </h4>
                <p className='text-sm leading-6 text-amber-800 dark:text-amber-200'>
                  Dealer Management is usually restricted to Dealership Admins and Super Admins
                  because changing dealer visibility affects everyone in your organization. If you
                  need access, contact your system administrator.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Understanding the Dealer List Section */}
        <section className='mb-20' id='understanding-the-dealer-list'>
          <h2 className='text-3xl font-bold mb-6'>Understanding the Dealer List</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            The Dealer Management page shows a table with all your dealers. Each row includes:
          </p>

          <div className='space-y-4'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <ul className='space-y-4'>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Dealer Name:</strong>{' '}
                    <span className='text-muted-foreground'>The official business name</span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Address:</strong>{' '}
                    <span className='text-muted-foreground'>
                      Physical location (street, city, state, ZIP)
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Contact Info:</strong>{' '}
                    <span className='text-muted-foreground'>Phone number and email</span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Status:</strong>{' '}
                    <span className='text-muted-foreground'>
                      Active (operating normally) or Inactive (closed/not operating)
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Recall Count:</strong>{' '}
                    <span className='text-muted-foreground'>
                      How many open recalls are at this location
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Include/Exclude Toggle:</strong>{' '}
                    <span className='text-muted-foreground'>
                      Whether this dealer appears in searches and routing
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className='bg-muted border-l-4 border-border p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Helpful Tip</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Click on any dealer row to expand it and see more details, including notes and
                  full contact information.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Include vs. Exclude Section */}
        <section className='mb-20' id='include-vs-exclude-whats-the-difference'>
          <h2 className='text-3xl font-bold mb-6'>Include vs. Exclude: What's the Difference?</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            This is the most important setting for each dealer. It controls whether they appear in
            your system:
          </p>

          <div className='mb-10'>
            <ComparisonTable
              rowHeaders={[
                'Appears in search results',
                'Shows on recall map',
                'Can assign recalls',
                'Shows in dealer filters',
                'Visible to technicians'
              ]}
              columns={[
                {
                  header: 'Include (Default)',
                  values: [true, true, true, true, true]
                },
                {
                  header: 'Exclude',
                  values: [false, false, false, false, false]
                }
              ]}
            />
          </div>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>When to Use "Include"</h3>
              <p className='text-sm leading-6 text-muted-foreground mb-4'>
                <strong>Include</strong> means the dealer is visible and active in your system. Use
                this for:
              </p>
              <ul className='space-y-2 ml-6'>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Dealers you are currently serving
                </li>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Locations under contract for recall work
                </li>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Any dealer you want technicians to see and visit
                </li>
              </ul>
              <p className='text-sm italic text-muted-foreground mt-4'>
                This is the default setting - most dealers should be "Included".
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>When to Use "Exclude"</h3>
              <p className='text-sm leading-6 text-muted-foreground mb-4'>
                <strong>Exclude</strong> hides the dealer from your system. Use this sparingly for:
              </p>
              <ul className='space-y-2 ml-6'>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Dealers temporarily closed (renovation, natural disaster, ownership change in
                  progress)
                </li>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Locations that opted out of your mobile service program (they prefer doing recalls
                  in-house)
                </li>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Competitors' locations that somehow ended up in your data
                </li>
                <li className='text-sm leading-6 text-muted-foreground'>
                  Dealers you have stopped serving (contract ended, moved out of territory)
                </li>
              </ul>
            </div>
          </div>

          <div className='bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <AlertTriangle className='h-6 w-6 text-amber-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-amber-900 dark:text-amber-100 mb-2'>
                  Be Careful When Excluding
                </h4>
                <p className='text-sm leading-6 text-amber-800 dark:text-amber-200'>
                  When you exclude a dealer, any recalls at that location essentially disappear from
                  your system. Your technicians won't see them, the map won't show them, and they
                  won't appear in any lists. Make sure this is what you want before excluding! Add a
                  note explaining why you are excluding them.
                </p>
              </div>
            </div>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-6'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Best Practice
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  If a dealer is only temporarily unavailable (like closed for 2 weeks for
                  renovations), exclude them and add a note: "Excluded: Closed for renovation until
                  [date]". Set a reminder to re-include them when they reopen.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Search Modes Section */}
        <section className='mb-20' id='search-modes-partial-match-vs-exact-word'>
          <h2 className='text-3xl font-bold mb-6'>Search Modes: Partial Match vs. Exact Word</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Vehix offers two ways to search for dealers. Understanding the difference helps you find
            what you need faster:
          </p>

          <div className='space-y-6'>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Search className='h-8 w-8 text-primary flex-shrink-0 mt-1' />
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold mb-3'>Partial Match (Default)</h3>
                    <p className='text-sm text-muted-foreground mb-4 leading-6'>
                      Searches for your text anywhere inside dealer names.
                    </p>
                    <div className='space-y-3 text-sm leading-6'>
                      <p className='text-foreground'>
                        <strong>Example:</strong> Search "Honda"
                      </p>
                      <p className='text-muted-foreground'>
                        ✓ Finds: "Victory Honda", "Honda of Miami", "HondaCorp Dealers"
                      </p>
                      <p className='text-muted-foreground'>
                        ✓ Finds: "Premier Automotive Honda Division"
                      </p>
                      <p className='text-sm italic text-muted-foreground mt-3'>
                        Use this when: You're not sure of the exact dealer name or want to find all
                        dealers with a certain word
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <div className='flex items-start gap-4'>
                  <Search className='h-8 w-8 text-primary flex-shrink-0 mt-1' />
                  <div className='flex-1'>
                    <h3 className='text-lg font-semibold mb-3'>Exact Word</h3>
                    <p className='text-sm text-muted-foreground mb-4 leading-6'>
                      Searches for your text as a complete, separate word.
                    </p>
                    <div className='space-y-3 text-sm leading-6'>
                      <p className='text-foreground'>
                        <strong>Example:</strong> Search "Honda"
                      </p>
                      <p className='text-muted-foreground'>
                        ✓ Finds: "Victory Honda", "Honda of Miami"
                      </p>
                      <p className='text-muted-foreground'>
                        ✗ Does NOT find: "HondaCorp" (Honda is part of a larger word)
                      </p>
                      <p className='text-sm italic text-muted-foreground mt-3'>
                        Use this when: You know the exact brand name and want precise results
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className='bg-muted border-l-4 border-border p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-foreground mb-2'>Pro Tip</h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Start with Partial Match for your first search. If you get too many results,
                  switch to Exact Word to narrow it down.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Adding Notes to Dealers Section */}
        <section className='mb-20' id='adding-notes-to-dealers'>
          <h2 className='text-3xl font-bold mb-6'>Adding Notes to Dealers</h2>
          <p className='text-sm text-muted-foreground mb-10 leading-6'>
            Dealer notes are internal comments visible only to your team. They help you remember
            important details about each location:
          </p>

          <div className='space-y-6'>
            <h3 className='text-lg font-semibold mb-4'>Great Things to Put in Notes:</h3>
            <Card className='rounded-xl border shadow-sm'>
              <CardContent className='p-6'>
                <ul className='space-y-4'>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Access instructions:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Gate code is 1234#, enter through rear service entrance"
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Best contact:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Ask for Mike in service, ext 205 - he handles all recalls"
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Scheduling preferences:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Only accepts mobile service Mon-Wed mornings"
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Parking/logistics:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Park in visitor spots, NOT in customer parking - they will tow!"
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Special requirements:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Requires proof of insurance before allowing work"
                    </span>
                  </li>
                  <li className='text-sm leading-6'>
                    <strong className='text-foreground'>Why excluded:</strong>{' '}
                    <span className='text-muted-foreground'>
                      "Excluded until July - under new management, renegotiating contract"
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Make Notes Specific and Actionable
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Instead of "difficult dealer", write "Requires 48-hour advance notice, call
                  service manager directly at XXX-XXX-XXXX".
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Workflows Section */}
        <section className='mb-20' id='common-dealer-management-workflows'>
          <h2 className='text-3xl font-bold mb-10'>Common Dealer Management Workflows</h2>

          <div className='space-y-12'>
            {/* Workflow 1 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Workflow 1: Temporarily Exclude a Dealer
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                A dealer is closed for renovation for 2 weeks. Here's how to handle it:
              </p>

              <div className='space-y-6'>
                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    1
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Search for the Dealer
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Use the search box to find the dealer by name.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    2
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Click to Expand</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Click on the dealer row to see all details and options.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    3
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Toggle to Exclude
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Find the Include/Exclude toggle switch and change it to "Exclude". The dealer
                      will immediately stop appearing in searches and maps.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    4
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Add a Note Explaining Why
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Click in the notes field and add: "Excluded: Closed for renovation until
                      [date]. Contact: [name/phone] for updates."
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    5
                  </div>
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Save Changes</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Click Save. Set a calendar reminder to re-include them when they reopen.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Workflow 2 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Workflow 2: Update Dealer Contact Information
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                A dealer got a new phone number or service manager. Keep your info current:
              </p>

              <div className='space-y-6'>
                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    1
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Locate the Dealer
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Search for and click on the dealer.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    2
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Click Edit</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Look for an Edit or pencil icon to enter edit mode.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    3
                  </div>
                  <div className='absolute left-6 top-12 bottom-0 w-0.5 bg-border -translate-x-1/2' />
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>
                      Update Contact Info
                    </h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Change the phone number, email, or add a note about the new contact person.
                    </p>
                  </div>
                </div>

                <div className='relative flex gap-4'>
                  <div className='flex-shrink-0 w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg'>
                    4
                  </div>
                  <div className='flex-1 pb-2'>
                    <h4 className='text-lg font-semibold text-foreground mb-2'>Save and Verify</h4>
                    <p className='text-sm leading-6 text-muted-foreground'>
                      Save your changes and verify the new information displays correctly. Notify
                      your team if this is a frequently-visited dealer.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Workflow 3 */}
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Workflow 3: Adding Notes About Service Preferences
              </h3>
              <p className='text-sm text-muted-foreground mb-6 leading-6'>
                You learn a dealer has specific requirements. Document them for your team:
              </p>

              <Card className='rounded-xl border shadow-sm'>
                <CardContent className='p-6'>
                  <ol className='space-y-3'>
                    <li className='text-sm leading-6 text-muted-foreground'>
                      Find and expand the dealer in the list
                    </li>
                    <li className='text-sm leading-6 text-muted-foreground'>
                      Click in the Notes field
                    </li>
                    <li className='text-sm leading-6 text-muted-foreground'>
                      Add clear, specific information (e.g., "Mobile service only on Tuesdays after
                      1 PM. Call service desk 555-0123 day before to confirm.")
                    </li>
                    <li className='text-sm leading-6 text-muted-foreground'>Save the note</li>
                    <li className='text-sm leading-6 text-muted-foreground'>
                      Optional: Share the note with your technicians via email or team meeting so
                      they know about the preference
                    </li>
                  </ol>
                </CardContent>
              </Card>
            </div>
          </div>

          <div className='bg-muted border-l-4 border-border p-6 rounded-r-xl mt-10'>
            <div className='flex gap-4'>
              <FileText className='h-6 w-6 text-muted-foreground flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-foreground mb-2'>
                  Notes are Visible to Your Whole Team
                </h4>
                <p className='text-sm leading-6 text-muted-foreground'>
                  Remember that anyone with access to Dealer Management can see your notes. Keep
                  them professional and factual. Don't write anything you wouldn't want a
                  technician, manager, or even the dealer to see.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Reference Section */}
        <section className='mb-20' id='quick-reference'>
          <h2 className='text-3xl font-bold mb-10'>Quick Reference</h2>
          <Card className='rounded-xl border shadow-sm'>
            <CardHeader>
              <CardTitle className='text-lg'>Dealer Management Quick Guide</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-4'>
                <div className='flex items-start justify-between gap-6 text-sm'>
                  <div className='flex-1'>
                    <span className='text-muted-foreground'>
                      Dealer visible everywhere - default for active dealers
                    </span>
                  </div>
                  <code className='px-3 py-1.5 text-sm font-mono bg-muted border border-border rounded flex-shrink-0'>
                    Include
                  </code>
                </div>
                <div className='flex items-start justify-between gap-6 text-sm'>
                  <div className='flex-1'>
                    <span className='text-muted-foreground'>
                      Dealer hidden from system - use temporarily only
                    </span>
                  </div>
                  <code className='px-3 py-1.5 text-sm font-mono bg-muted border border-border rounded flex-shrink-0'>
                    Exclude
                  </code>
                </div>
                <div className='flex items-start justify-between gap-6 text-sm'>
                  <div className='flex-1'>
                    <span className='text-muted-foreground'>
                      Search anywhere in name - finds "Honda" in "HondaCorp"
                    </span>
                  </div>
                  <code className='px-3 py-1.5 text-sm font-mono bg-muted border border-border rounded flex-shrink-0'>
                    Partial Match
                  </code>
                </div>
                <div className='flex items-start justify-between gap-6 text-sm'>
                  <div className='flex-1'>
                    <span className='text-muted-foreground'>
                      Search complete words only - more precise results
                    </span>
                  </div>
                  <code className='px-3 py-1.5 text-sm font-mono bg-muted border border-border rounded flex-shrink-0'>
                    Exact Word
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Common Questions Section */}
        <section className='mb-20' id='common-questions'>
          <h2 className='text-3xl font-bold mb-10'>Common Questions</h2>

          <div className='space-y-10'>
            <div>
              <h3 className='text-lg font-semibold mb-4'>
                If I exclude a dealer, what happens to their recalls?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                The recalls still exist in the database, but they become invisible to your team.
                They won't show on maps, lists, or in routing suggestions. If you re-include the
                dealer later, the recalls reappear as if nothing happened.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>Can I permanently delete a dealer?</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                No. Vehix does not allow deleting dealers because that could lose important
                historical data. Instead, use "Exclude" to hide dealers you are no longer working
                with. This keeps your history intact while cleaning up your active lists.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>How do I see only excluded dealers?</h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                Look for a filter or toggle at the top of the Dealer Management page that says "Show
                Excluded" or similar. This lets you review dealers you have hidden and re-include
                them if needed.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                What if dealer information is wrong (address, name, etc.)?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                You can update contact information and notes, but core data like dealer name and
                address usually comes from manufacturer data feeds. If core information is wrong,
                contact{' '}
                <a href='mailto:contact@kebi.ai' className='text-primary hover:underline'>
                  contact@kebi.ai
                </a>{' '}
                - they can investigate and update the source data.
              </p>
            </div>

            <div>
              <h3 className='text-lg font-semibold mb-4'>
                Can I add new dealers that aren't in the system?
              </h3>
              <p className='text-sm leading-6 text-muted-foreground'>
                This depends on your Vehix configuration. Some systems allow adding custom dealers,
                others only use manufacturer-provided dealer lists. If you need to add a dealer,
                contact your administrator or{' '}
                <a href='mailto:contact@kebi.ai' className='text-primary hover:underline'>
                  contact@kebi.ai
                </a>
                .
              </p>
            </div>
          </div>
        </section>

        {/* Closing Tips Section */}
        <div className='space-y-6'>
          <div className='bg-green-50 dark:bg-green-950/30 border-l-4 border-green-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <CheckCircle className='h-6 w-6 text-green-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-green-900 dark:text-green-100 mb-2'>
                  Regular Maintenance Tip
                </h4>
                <p className='text-sm leading-6 text-green-800 dark:text-green-200'>
                  Review your dealer list quarterly. Re-include dealers that have reopened, exclude
                  ones that closed, and update notes as needed. Keep your data fresh!
                </p>
              </div>
            </div>
          </div>

          <div className='bg-blue-50 dark:bg-blue-950/30 border-l-4 border-blue-600 p-6 rounded-r-xl'>
            <div className='flex gap-4'>
              <Info className='h-6 w-6 text-blue-600 flex-shrink-0 mt-1' />
              <div>
                <h4 className='text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2'>
                  Need Help?
                </h4>
                <p className='text-sm leading-6 text-blue-800 dark:text-blue-200'>
                  Dealer Management affects everyone in your organization. If you are unsure whether
                  to include or exclude a dealer, or if you need help with search features, contact{' '}
                  <a
                    href='mailto:contact@kebi.ai'
                    className='underline hover:no-underline font-semibold'
                  >
                    contact@kebi.ai
                  </a>{' '}
                  for guidance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DocsLayout>
  );
};
